import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import type { PersonProfile, DirectoryPerson } from "@sales-ops-amplify/shared";
import { enrichPeople, savePeople } from "@/api/people";
import { LeadEditForm } from "@/components/companies/lead-panel";
import { PersonContactRail } from "@/components/people/person-info-rail";
import {
  isPersonTabId,
  PersonHero,
  type PersonTabId,
} from "@/components/people/person-hero";
import { PersonTabs } from "@/components/people/person-tabs";
import { PersonActivitiesTab } from "@/components/people/tabs/person-activities-tab";
import { PersonConversationsTab } from "@/components/people/tabs/person-conversations-tab";
import { PersonEnrichmentTab } from "@/components/people/tabs/person-enrichment-tab";
import { PersonFieldsTab } from "@/components/people/tabs/person-fields-tab";
import { PersonOverviewTab } from "@/components/people/tabs/person-overview-tab";
import { PersonSequencesTab } from "@/components/people/tabs/person-sequences-tab";
import { usePersonProfile } from "@/hooks/usePerson";
import { usePersonDetailRoute } from "@/lib/person-detail-route";

function profileToDirectoryPerson(profile: PersonProfile): DirectoryPerson {
  return {
    personId: profile.personId,
    apolloPersonId: profile.apolloPersonId,
    leadId: profile.leadId,
    accountId: profile.accountId,
    firstName: profile.firstName,
    lastName: profile.lastName,
    name: profile.name,
    title: profile.title,
    seniority: profile.seniority,
    departments: profile.departments,
    email: profile.email,
    emailStatus: profile.emailStatus ?? "unknown",
    phone: profile.phone,
    phoneStatus: profile.phoneStatus ?? "unknown",
    linkedinUrl: profile.linkedinUrl,
    city: profile.city,
    state: profile.state,
    country: profile.country,
    hasEmail: !!profile.email || profile.emails.length > 0,
    hasDirectPhone: !!profile.phone || profile.phones.length > 0,
    organization: {
      companyId: profile.organization.companyId,
      apolloOrgId: profile.organization.apolloOrgId,
      name: profile.organization.name,
      domain: profile.organization.domain,
      industry: profile.organization.industry,
      employeeCount: profile.organization.employeeCount,
      linkedinUrl: profile.organization.linkedinUrl,
      websiteUrl: profile.organization.websiteUrl,
    },
    alreadySaved: profile.alreadySaved,
    stage: profile.stage,
    leadOwner: profile.leadOwner,
    mobilePhone: profile.mobilePhone,
    companyId: profile.organization.companyId,
    subDepartments: profile.subDepartments,
  };
}

export function PersonDetailPage() {
  const { detailId, isMutable, peopleBase, tab: tabParam } =
    usePersonDetailRoute();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const activeTab: PersonTabId = isPersonTabId(tabParam) ? tabParam : "overview";
  const [editing, setEditing] = useState(false);

  const { data: profile, isLoading, error } = usePersonProfile(detailId);

  const canonicalLeadId = profile?.user?.leadId ?? profile?.leadId;

  useEffect(() => {
    if (isLoading || !isMutable || !canonicalLeadId || !detailId) return;
    if (canonicalLeadId === detailId) return;
    const suffix = activeTab === "overview" ? "" : `/${activeTab}`;
    navigate(`/user/leads/${canonicalLeadId}${suffix}`, { replace: true });
  }, [
    isLoading,
    isMutable,
    canonicalLeadId,
    detailId,
    activeTab,
    navigate,
  ]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!profile || !detailId) return;
      return savePeople({
        people: [profileToDirectoryPerson(profile)],
        saveLeads: true,
        ensureCompanies: true,
      });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["people", "profile", detailId] });
      const savedId = res?.leads?.[0]?.leadId ?? res?.people?.[0]?.leadId;
      if (savedId) navigate(`/user/leads/${savedId}`);
    },
  });

  const enrichMutation = useMutation({
    mutationFn: async () => {
      if (!profile || !detailId) return;
      return enrichPeople({
        people: [profileToDirectoryPerson(profile)],
        leadIds: profile.leadId ? [profile.leadId] : undefined,
        revealEmail: true,
        revealPhone: true,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["people", "profile", detailId] });
      void qc.invalidateQueries({
        queryKey: ["people", "activities", detailId],
      });
    },
  });

  function setTab(next: PersonTabId) {
    if (!detailId) return;
    const id = isMutable ? (canonicalLeadId ?? detailId) : detailId;
    const base = `${peopleBase}/${id}`;
    navigate(next === "overview" ? base : `${base}/${next}`);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (
    error ||
    !profile ||
    !detailId ||
    (isMutable && !profile.user && !profile.leadId)
  ) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Link
          to={peopleBase}
          className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline"
        >
          {isMutable ? "Back to Leads" : "Back to Find people"}
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Person not found.</p>
        </div>
      </div>
    );
  }

  const companyHref = isMutable
    ? profile.accountId
      ? `/user/accounts/${profile.accountId}`
      : undefined
    : profile.organization.companyId
      ? `/companies/${profile.organization.companyId}`
      : undefined;

  return (
    <div className="mx-auto w-full min-w-0 max-w-7xl space-y-5 overflow-x-hidden">
      <PersonHero
        profile={profile}
        isMutable={isMutable}
        peopleBase={peopleBase}
        companyHref={companyHref}
        onSave={() => saveMutation.mutate()}
        onEnrich={() => enrichMutation.mutate()}
        onAddToList={() => navigate(isMutable ? "/user/leads" : "/people")}
        onAddToSequence={() => navigate("/sequences")}
        saving={saveMutation.isPending}
        enriching={enrichMutation.isPending}
      />

      <div className="grid min-w-0 gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <PersonContactRail
          profile={profile}
          companyHref={companyHref}
          onEnrich={() => enrichMutation.mutate()}
          enriching={enrichMutation.isPending}
        />

        <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden">
          {isMutable && profile.user ? (
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">CRM profile</h3>
                <button
                  type="button"
                  onClick={() => setEditing((v) => !v)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted/40"
                >
                  {editing ? "Close editor" : "Edit"}
                </button>
              </div>
              {editing ? (
                <LeadEditForm
                  contact={profile.user}
                  onCancel={() => setEditing(false)}
                  onSaved={() => {
                    setEditing(false);
                    void qc.invalidateQueries({
                      queryKey: ["people", "profile", detailId],
                    });
                  }}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Edit CRM fields and external links (SalesNav, Jira, etc.) for
                  this person.
                </p>
              )}
            </div>
          ) : null}

          <PersonTabs active={activeTab} onChange={setTab} />
          <div role="tabpanel" className="min-h-[320px]">
            {activeTab === "overview" ? (
              <PersonOverviewTab
                profile={profile}
                companyHref={companyHref}
              />
            ) : null}
            {activeTab === "activities" ? (
              <PersonActivitiesTab personId={detailId} profile={profile} />
            ) : null}
            {activeTab === "sequences" ? (
              <PersonSequencesTab leadId={profile.leadId} />
            ) : null}
            {activeTab === "conversations" ? (
              <PersonConversationsTab />
            ) : null}
            {activeTab === "enrichment" ? (
              <PersonEnrichmentTab
                profile={profile}
                onEnrich={() => enrichMutation.mutate()}
                enriching={enrichMutation.isPending}
              />
            ) : null}
            {activeTab === "fields" ? (
              <PersonFieldsTab profile={profile} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
